import { Calendar } from "@patient-watch/ui/calendar";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@patient-watch/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@patient-watch/ui/popover";
import { cn } from "@patient-watch/ui";
import dayjs from "dayjs";
import { ChevronDown } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { RichLabel } from "./components/RichLabel";
import type { LooseRHFField } from "./utils/loose-rhf-field";

/** Matches `SelectTrigger` size=md so date fields sit flush beside selects. */
const selectLikeTriggerClassName =
    "flex justify-between items-center gap-2 bg-white hover:bg-white dark:bg-slate-900 dark:hover:bg-slate-900 disabled:opacity-50 px-3 py-2 border border-slate-300 dark:border-slate-500 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-300 ring-offset-white dark:ring-offset-indigo-950 w-full h-12 text-slate-800 dark:text-slate-200 text-sm disabled:cursor-not-allowed";

interface Props {
    name: string;
    label?: string;
    description?: string;
    disabled?: boolean;
    required?: boolean;
}

const DateInput = ({ name = "date", label, description, disabled, required = false }: Props) => {
    const { control } = useFormContext();

    return (
        <FormField
            control={control}
            name={name}
            render={({ field }: { field: LooseRHFField }) => (
                <FormItem className="flex flex-col text-sm">
                    {label && (
                        <FormLabel>
                            <RichLabel>{label}</RichLabel>
                            {required && <span className="ml-0.5 text-red-500">*</span>}
                        </FormLabel>
                    )}
                    {!!description && <FormDescription>{description}</FormDescription>}
                    <Popover>
                        <PopoverTrigger asChild>
                            <FormControl>
                                <button
                                    type="button"
                                    disabled={disabled}
                                    className={cn(
                                        selectLikeTriggerClassName,
                                        !field.value && "text-slate-500 dark:text-slate-400"
                                    )}
                                >
                                    <span className="text-left line-clamp-1">
                                        {field.value ? dayjs(field.value as Date).format("D MMMM YYYY") : "Pick a date"}
                                    </span>
                                    <ChevronDown className="opacity-75 w-4 h-4 shrink-0" />
                                </button>
                            </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="p-0 w-auto" align="start">
                            <Calendar
                                mode="single"
                                selected={field.value instanceof Date ? field.value : undefined}
                                onSelect={(date) => {
                                    const dateWithNoon = dayjs(date).set("hour", 12).set("minute", 0).toDate();
                                    field.onChange(dateWithNoon);
                                }}
                                disabled={disabled ?? false}
                                initialFocus
                            />
                        </PopoverContent>
                    </Popover>
                    <FormMessage />
                </FormItem>
            )}
        />
    );
};

export default DateInput;
